import * as React from 'react'
import { connect } from 'react-redux'
import type { RouteComponentProps } from 'react-router'
import Client from '../../../../client'
import WithNotFound from '../../../../components/AppError/WithNotFound'
import Errors from '../../../../components/Errors'
import ImageUploader from '../../../../components/images/Uploader'
import Spinner from '../../../../components/Spinner'
import Widget from '../../../../components/Widget'
import config from '../../../../config'
import { AppState } from '../../../../store'
import { debounce } from '../../../../utils'
import { parseSpecificsErrors } from '../../../products_v5/components/JoinedForm/specificsHelpers'
import Form from './Form'
import { getHammoqAttributesAutofilled } from '../../../products_v5/components/JoinedForm/Helpers'

interface Props {
  parentSetState: any
  parentOnFormChange: (key: string, value: any, callback: () => void) => void
  isNewProductListing?: boolean
  validateConcurrent?: boolean
  moveToMarket?: boolean
  loading?: boolean
  error: any
  ebayMessage: any
  images: any
  deletedImages: any
  product: any
  listingId: any
  marketId: number
  marketAccountIdQueryParam: number
  form: any
  savedStartTime: any
  listingStrategyId: any
  requirements: any
  enableTaxTable: boolean
}

interface State {
  hasFetchedSpecificsOnce: boolean
}

// A parent component is responsible for the simple state management of form field values, form submission, etc.
// This is responsible for state management:
// - which is localized to certain fields (such as making API calls to fetch possible values)
// - involving interactions between fields (such as debouncing specifics when the category changes)
// - basically everything that we don't need to pull into the higher level which handles form submission
class EbayListingForm extends React.Component<Props & RouteComponentProps<{ id?: string; listingId?: string }>, State> {
  constructor(props) {
    super(props)
    this.state = {
      hasFetchedSpecificsOnce: false
    }

    if (this.props.form.id || this.props.form?.categoryMarketCategory?.id) {
      this.bootstrapEbayInfo()
      this.setState({ hasFetchedSpecificsOnce: true })
    }
  }

  resetHasFetchedSpecificsOnce = () =>
    this.setState(prevState => ({
      hasFetchedSpecificsOnce: !prevState.hasFetchedSpecificsOnce
    }))

  componentDidUpdate = prevProps => {
    if (prevProps.form?.id !== this.props.form?.id) {
      this.resetHasFetchedSpecificsOnce()
    }

    if (
      !this.state.hasFetchedSpecificsOnce &&
      this.props.form?.categoryMarketCategory?.id != null &&
      this.props?.requirements?.specifics.length === 0 &&
      !this.props?.requirements?.specifics?.loading
    ) {
      this.bootstrapEbayInfo()
    }
  }

  loadListingStrategyConfigurationValues = async () => {
    if (!this.props.listingStrategyId) return
    try {
      const res = await Client.listingStrategyConfigurationLookup(this.props.listingStrategyId, config.MARKETS.ebay)
      const { configuration } = res.data
      if (!configuration) return
      const { form } = this.props
      const startingPrice =
        configuration.priceChange && configuration.priceChangeBy === 'SET' ? configuration.priceChange : form.price
      const buyItNowPrice =
        configuration.buyItNowPriceChange && configuration.buyItNowPriceChangeBy === 'SET'
          ? configuration.buyItNowPriceChange
          : form.buyItNowPrice
      const reservePrice =
        configuration.reservePriceChange && configuration.reservePriceChangeBy === 'SET'
          ? configuration.reservePriceChange
          : form.reservePrice

      const listingDuration = configuration.listingDuration ? configuration.listingDuration : form.listingDuration
      const listingType = configuration.listingType ? configuration.listingType : form.listingType

      this.props.parentSetState(s => ({
        form: {
          ...s.form,
          startingPrice,
          buyItNowPrice,
          reservePrice,
          listingDuration,
          listingType
        }
      }))
    } catch (_error) {
      // Not a critical call, do nothing on error
    }
  }

  onListingStrategyChange = listingStrategyId => {
    this.props.parentSetState(() => ({ listingStrategyId }), this.loadListingStrategyConfigurationValues)
  }

  onImageChange = (images, cb: any = () => {}) => {
    this.props.parentSetState(
      s => ({
        images: images.filter(i => !i.removed),
        deletedImages: s.deletedImages.concat(images.filter(i => i.removed))
      }),
      () => cb(this.props.images)
    )
  }

  bootstrapEbayInfo = () => {
    if (this.state.hasFetchedSpecificsOnce) return

    this.resetHasFetchedSpecificsOnce()

    const cmcID = this.props.form.categoryMarketCategory?.id
    this.loadSpecifics(cmcID)
  }

  onFormChange = (key, value) => {
    this.props.parentOnFormChange(key, value, () => {
      if (key === 'categoryMarketCategory') {
        this.debounceSpecifics(this.props.form.categoryMarketCategory?.id)
      }
    })
  }

  onBestOfferChange = value => {
    if (value) {
      this.props.parentSetState(s => ({
        form: {
          ...s.form,
          bestOfferEnabled: true
        }
      }))
    } else {
      this.props.parentSetState(s => ({
        form: {
          ...s.form,
          bestOfferEnabled: false,
          bestOfferAutoAcceptPrice: null,
          minimumBestOfferPrice: null
        }
      }))
    }
  }

  onProductChange = (key, value) => {
    this.props.parentSetState(s => ({
      product: {
        ...s.product,
        [key]: value
      }
    }))
  }

  onSpecificChange = (value, specificIndex, requiresProduct = false) => {
    const { form, requirements } = this.props
    const specific = requirements.specifics[specificIndex]
    form.specifics = Object.assign(form.specifics, { [specific.name]: value })
    requirements.epidRequired = requirements.epidRequired || requiresProduct

    this.props.parentSetState({
      form,
      requirements
    })
  }

  onRefreshSpecifics = async () => {
    this.props.parentSetState(state => ({ requirements: { ...state.requirements, loading: true, error: null } }))
    const cmcID = this.props.form.categoryMarketCategory?.id
    try {
      await Client.resetCategorySpecifics(cmcID)
      this.loadSpecifics(cmcID)
    } catch (_error) {
      this.props.parentSetState(state => ({
        requirements: {
          ...state.requirements,
          loading: false,
          error: 'Something went wrong when refreshing specifics. Please click "Refresh" to try again.'
        }
      }))
    }
  }

  onProductDetailChange = (key, value) => {
    this.props.parentSetState(s => ({
      form: {
        ...s.form,
        productListingDetails: {
          ...s.form.productListingDetails,
          [key]: value
        }
      }
    }))
  }

  loadSpecifics = async categoryMarketCategoryId => {
    this.props.parentSetState(state => ({ requirements: { ...state.requirements, loading: true, error: null } }))
    try {
      const requirements = (
        await Promise.all([
          Client.getEbayCategoryFeatures(categoryMarketCategoryId),
          Client.categorySpecifics(categoryMarketCategoryId),
          Client.getNotes('Product', this.props.form.productId)
        ])
      )
        .map(r => r && r.data)
        .reduce((acc, curr) => Object.assign(acc, curr), {})

      requirements.epidRequired = this.props.requirements.epidRequired

      const autofilledSpecifics = requirements.notes // potentially has Hammoq Attributes for Ebay Specifics
        ? getHammoqAttributesAutofilled(requirements.specifics, requirements.notes)
        : {}

      if (config.EBAY_AUTOFILL_SPECIFICS()) {
        if (Object.keys(autofilledSpecifics).length > 0) {
          this.props.form.specifics = { ...autofilledSpecifics, ...this.props.form.specifics }
        }
      }
      this.props.parentSetState({
        requirements: {
          loading: false,
          specifics: [],
          products: [],
          ...requirements,
          conditions: requirements.features.conditions || [],
          bestOfferEnabled: requirements.features.bestOfferEnabled,
          upc: requirements.features.upc,
          ean: requirements.features.ean,
          isbn: requirements.features.isbn
        }
      })
    } catch (error) {
      const { errors } = error.response.data

      this.props.parentSetState(state => ({
        requirements: {
          ...state.requirements,
          loading: false,
          error: parseSpecificsErrors(errors)
        }
      }))
    }
  }

  debounceSpecifics = debounce(async categoryMarketCategoryId => {
    this.loadSpecifics(categoryMarketCategoryId)
  }, 1000)

  onCategoryChange = categoryMarketCategory => {
    this.props.parentSetState(
      s => ({
        requirements: {
          ...s.requirements,
          epidRequired: false
        },
        form: {
          ...s.form,
          categoryMarketCategory
        }
      }),
      () => this.debounceSpecifics(this.props.form.categoryMarketCategory?.id)
    )
  }

  filteredError = () => {
    if (!this.props.error) return null
    const { dispatchTimeMax, ...otherErrors } = (this.props.error && this.props.error.errors) || {}
    if (dispatchTimeMax) otherErrors.handlingTime = dispatchTimeMax
    return { ...this.props.error, errors: otherErrors }
  }

  doOfferAdditionalInfo = () => {
    const { state } = this.props.form

    return state === 'LISTED'
  }

  render() {
    if (this.props.loading) {
      return <Spinner />
    }

    return (
      <>
        <Errors
          error={this.filteredError()}
          message={this.props.ebayMessage}
          messageHeader="Additional Information From eBay"
        />
        <Widget title="Images">
          <ImageUploader images={this.props.images} onChange={this.onImageChange} />
        </Widget>
        <Widget title={this.props.form.accFullHumanReadableName || 'Ebay'}>
          <Form
            error={this.props.error}
            requirements={this.props.requirements}
            form={this.props.form}
            product={this.props.product}
            onFormChange={this.onFormChange}
            onBestOfferChange={this.onBestOfferChange}
            onProductChange={this.onProductChange}
            onSpecificChange={this.onSpecificChange}
            onCategoryChange={this.onCategoryChange}
            onRefreshSpecifics={this.onRefreshSpecifics}
            listingExists={!!this.props.listingId}
            savedStartTime={this.props.savedStartTime}
            onProductDetailChange={this.onProductDetailChange}
            onListingStrategyChange={this.onListingStrategyChange}
            listingStrategyId={this.props.listingStrategyId}
            listingStrategyDisabled={!!this.props.listingId || this.props.isNewProductListing}
            enableTaxTable={this.props.enableTaxTable}
            doOfferAdditionalInfo={this.doOfferAdditionalInfo()}
          />
        </Widget>
      </>
    )
  }
}

export const EbayListing = connect((state: AppState) => ({ current: state.current }))(WithNotFound(EbayListingForm))
